<template>
  <div
    v-if="pageType === 'school-detail'"
    class="inter-level2">
    <!-- Contenu détail école -->
    <SchoolDetail
      :school-data="schoolData ?? null"
      :loading="schoolLoading"
      :is-logged-in="isLoggedIn" />
  </div>
  <div
    v-else-if="pageType === 'programs-list'"
    class="inter-level3">
    <!-- Contenu liste programmes -->
    <SchoolPrograms
      :school-programs="schoolPrograms ?? null"
      :loading="programsLoading"
      :is-logged-in="isLoggedIn" />
  </div>
  <div
    v-else-if="pageType === 'program-detail'"
    class="inter-level4">
    <!-- Contenu détail programme -->
    <ProgramDetail
      :program-data="programData ?? null"
      :loading="programLoading"
      :is-logged-in="isLoggedIn" />
  </div>
</template>

<script setup lang="ts">
import type { ComputedRef } from 'vue'
import { computed } from 'vue'
import { useSchoolData } from '~/composables/useSchoolData'
import { useSchoolPrograms } from '~/composables/useSchoolPrograms'
import { useProgramData } from '~/composables/useProgramData'
import { useSeoParentRedirect } from '~/composables/useSeoParentRedirect'
import SchoolDetail from '~/components/aca/SchoolDetail.vue'
import SchoolPrograms from '~/components/aca/SchoolPrograms.vue'
import ProgramDetail from '~/components/aca/ProgramDetail.vue'
import type {
  ProgramDataResponse,
  SchoolDataResponse,
  SchoolProgramsResponse,
} from '~/types/school'
import {
  isEmptyProgramApiPayload,
  isEmptySchoolProgramsApiPayload,
  isShellSchoolDataResponse,
  shouldRedirectToSeoParent,
} from '~/utils/seoDeadPayloads'

const { locale } = useI18n()
const route = useRoute()

// Reconstitue le tableau de segments depuis l'URL // Ex: /ou-etudier/france/paris → ['france', 'paris']
const pathSegments: ComputedRef<string[]> = computed(() => {
  const p: string | string[] | undefined = route.params.slug
  return Array.isArray(p) ? p : p ? [p] : []
})

// Selon le nombre de segments, affiche du contenu différent
const pageType = computed(() => {
  if (pathSegments.value.length === 1) return 'school-detail'
  if (pathSegments.value.length === 2) return 'programs-list'
  if (pathSegments.value.length === 3) return 'program-detail'
})

const alias = computed(() => pathSegments.value.join('/'))

// `await` requis pour que Nuxt attende le fetch en SSR (payload hydraté).
const {
  schoolData,
  loading: schoolLoading,
  lastHttpStatus: schoolHttp,
} = await useSchoolData({
  language: locale,
  alias,
})

const {
  schoolPrograms,
  loading: programsLoading,
  lastHttpStatus: programsHttp,
} = await useSchoolPrograms({
  language: locale,
  alias,
})

const {
  programData,
  loading: programLoading,
  lastHttpStatus: programHttp,
} = await useProgramData({
  language: locale,
  alias,
})

const seoRedirectState = computed(() => {
  const pt = pageType.value
  if (pt === 'school-detail') {
    const loading: boolean = schoolLoading.value
    const isDead: boolean = shouldRedirectToSeoParent({
      loading,
      httpStatus: schoolHttp.value,
      data: schoolData.value,
      isEmptyPayload: (payload) => isShellSchoolDataResponse(payload as SchoolDataResponse),
    })
    return { loading, isDead }
  }
  if (pt === 'programs-list') {
    const loading: boolean = programsLoading.value
    const isDead: boolean = shouldRedirectToSeoParent({
      loading,
      httpStatus: programsHttp.value,
      data: schoolPrograms.value,
      isEmptyPayload: (payload) =>
        isEmptySchoolProgramsApiPayload((payload as SchoolProgramsResponse).schoolprograms),
    })
    return { loading, isDead }
  }
  if (pt === 'program-detail') {
    const loading: boolean = programLoading.value
    const isDead: boolean = shouldRedirectToSeoParent({
      loading,
      httpStatus: programHttp.value,
      data: programData.value,
      isEmptyPayload: (payload) =>
        isEmptyProgramApiPayload((payload as ProgramDataResponse).programdatas),
    })
    return { loading, isDead }
  }
  return { loading: false, isDead: false }
})

const { redirectIfDead: redirectSchoolIfDead } = useSeoParentRedirect({
  pathSegments: () => pathSegments.value,
  sectionRoot: 'ecoles',
  state: () => seoRedirectState.value,
  source: 'ecoles/[...slug]',
})

const schoolRedirect = redirectSchoolIfDead()
if (schoolRedirect) await schoolRedirect

// ── API & AUTH ───────────────────────────────────────────────
const { isLoggedIn } = useAuth()
const { canonicalUrl } = useSeo()

// ── PAGE META ───────────────────────────────────────────────
definePageMeta({
  layout: 'default',
})

switch (pageType.value) {
  case 'school-detail':
    // Contenu du détail d'une école
    // ── SEO / HEAD ───────────────────────────────────────────────
    useSeoMeta({
      title: computed(() => schoolData.value?.schooldatas.title),
      description: computed(() => schoolData.value?.schooldatas.description),
      ogTitle: computed(() => schoolData.value?.schooldatas.h1),
      ogDescription: computed(() => schoolData.value?.schooldatas.introduction),
      ogType: 'website',
    })

    // JSON-LD
    useHead({
      script: computed(() => [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: schoolData.value?.schooldatas.h1,
            description: schoolData.value?.schooldatas.introduction,
            url: canonicalUrl.value,
          }),
        },
      ]),
    })

    break

  case 'programs-list':
    // Contenu de la liste des programmes d'une école
    // ── SEO / HEAD ───────────────────────────────────────────────
    useSeoMeta({
      title: computed(() => schoolPrograms.value?.schoolprograms.title),
      description: computed(() => schoolPrograms.value?.schoolprograms.description),
      ogTitle: computed(() => schoolPrograms.value?.schoolprograms.h1),
      ogDescription: computed(() => schoolPrograms.value?.schoolprograms.introduction),
      ogType: 'website',
    })

    // JSON-LD
    useHead({
      script: computed(() => [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: schoolPrograms.value?.schoolprograms.h1,
            description: schoolPrograms.value?.schoolprograms.introduction,
            url: canonicalUrl.value,
          }),
        },
      ]),
    })
    break

  case 'program-detail':
    // Contenu du détail d'un programme
    // ── SEO / HEAD ───────────────────────────────────────────────
    useSeoMeta({
      title: computed(() => programData.value?.programdatas.title),
      description: computed(() => programData.value?.programdatas.description),
      ogTitle: computed(() => programData.value?.programdatas.h1),
      ogDescription: computed(() => programData.value?.programdatas.introduction),
      ogType: 'website',
    })

    // JSON-LD
    useHead({
      script: computed(() => [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: programData.value?.programdatas.h1,
            description: programData.value?.programdatas.introduction,
            url: canonicalUrl.value,
          }),
        },
      ]),
    })
    break

  default:
    // Page non trouvée ou autre cas
    break
}
</script>

<style scoped>
/* ════════════════════════════════════════════════════════════
   STYLES LEVEL 
   ════════════════════════════════════════════════════════════ */

/* ── VARIABLES LOCALES ─────────────────────────────────────────────── */
:global(body) {
  background-color: var(--bg-base);
}
</style>
